// Set-password screen for invited users.
//
// Supabase invite / password-reset emails redirect here with a token in the URL.
// supabase-js (detectSessionInUrl) consumes that token and establishes a
// temporary session, which lets us call updateUser({ password }). Once the
// password is set, we send the user into the app with a clean reload.
//
// App.tsx routes here whenever the path is /set-password, regardless of session
// state, so the token can be processed.

import { useEffect, useState } from 'react';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { supabase } from '../supabase';
import { C, RADIUS } from '../theme';

type Phase = 'checking' | 'ready' | 'no_session' | 'saving' | 'done';

export default function SetPassword() {
  const [phase, setPhase] = useState<Phase>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState(false); // reveal both password fields (defaults hidden)

  // Wait for supabase-js to turn the URL token into a session.
  useEffect(() => {
    let settled = false;
    const markReady = (hasSession: boolean) => {
      if (settled) return;
      settled = true;
      setPhase(hasSession ? 'ready' : 'no_session');
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) markReady(true);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) markReady(true);
    });
    // If no session has appeared shortly after load, the link is bad/expired.
    const timer = setTimeout(() => markReady(false), 4000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  async function handleSave() {
    setError(null);
    if (password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords don’t match.');
      return;
    }
    setPhase('saving');
    const { error: updErr } = await supabase.auth.updateUser({ password });
    if (updErr) {
      setError(updErr.message);
      setPhase('ready');
      return;
    }
    setPhase('done');
    // Full reload so the app re-initialises with the now-active session.
    setTimeout(() => {
      window.location.href = '/';
    }, 900);
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg, padding: 24 }}>
      <div style={{ width: 380, background: C.card, borderRadius: RADIUS.lg, padding: 36, border: `1px solid ${C.border}` }}>
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 500, fontStyle: 'italic', color: C.orange }}>Floxir</div>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 3 }}>Set your password</div>
        </div>

        {phase === 'checking' && (
          <div style={{ fontSize: 13, color: C.textMuted, textAlign: 'center' }}>Checking your invite…</div>
        )}

        {phase === 'no_session' && (
          <div style={{ fontSize: 13, color: C.textMuted, textAlign: 'center', lineHeight: 1.6 }}>
            This link is invalid or has expired. Ask the person who invited you to send a new invitation.
          </div>
        )}

        {phase === 'done' && (
          <div style={{ fontSize: 13, color: C.green, textAlign: 'center' }}>
            Password set — taking you in…
          </div>
        )}

        {(phase === 'ready' || phase === 'saving') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textDark, display: 'block', marginBottom: 5 }}>New password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  style={{ width: '100%', padding: '9px 40px 9px 12px', fontSize: 14, border: `1px solid ${C.border}`, borderRadius: RADIUS.md, outline: 'none', boxSizing: 'border-box' }}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  aria-label={show ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.textMuted, display: 'flex', alignItems: 'center' }}
                >
                  {show ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textDark, display: 'block', marginBottom: 5 }}>Confirm password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                  style={{ width: '100%', padding: '9px 40px 9px 12px', fontSize: 14, border: `1px solid ${C.border}`, borderRadius: RADIUS.md, outline: 'none', boxSizing: 'border-box' }}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  aria-label={show ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.textMuted, display: 'flex', alignItems: 'center' }}
                >
                  {show ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div style={{ fontSize: 12, color: C.red, background: C.redLight, padding: '8px 12px', borderRadius: RADIUS.md }}>
                {error}
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={phase === 'saving'}
              style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 600, color: '#fff', background: C.orange, border: 'none', borderRadius: RADIUS.md, cursor: phase === 'saving' ? 'default' : 'pointer', marginTop: 4 }}
            >
              {phase === 'saving' ? 'Saving…' : 'Set password & continue'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
